import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon/Icon';
import { usePrefetch } from '../../hooks/usePrefetch';
import { AppTool, filterTools } from '../../tools/appTools';
import './ToolsMenu.scss';

interface ToolsMenuProps {
  tools: AppTool[];
  active?: boolean;
}

const ToolsMenu: React.FC<ToolsMenuProps> = ({ tools, active = false }) => {
  const navigate = useNavigate();
  const { prefetchRoute } = usePrefetch();
  const listId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [box, setBox] = useState({ top: 0, left: 0, width: 300 });

  const results = useMemo(() => filterTools(tools, query), [tools, query]);

  useEffect(() => {
    setCursor(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(300, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setBox({ top: rect.bottom + 8, left, width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = () => {
      setOpen(false);
      setQuery('');
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      dismiss();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const toggle = () => {
    setOpen((value) => {
      const next = !value;
      if (!next) setQuery('');
      return next;
    });
  };

  const openTool = (tool: AppTool) => {
    close();
    navigate(tool.path);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((value) => (results.length === 0 ? 0 : (value + 1) % results.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((value) => (results.length === 0 ? 0 : (value - 1 + results.length) % results.length));
    } else if (event.key === 'Enter' && results[cursor]) {
      event.preventDefault();
      openTool(results[cursor]);
    }
  };

  const triggerClass = `header__nav-link tools-menu__trigger${active || open ? ' header__nav-link--active' : ''}${open ? ' is-open' : ''}`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={triggerClass}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={toggle}
      >
        Autres
        <Icon name="chevron-down" size={12} />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="tools-menu__panel"
          style={{ top: box.top, left: box.left, width: box.width }}
          role="presentation"
        >
          <label className="tools-menu__search">
            <Icon name="search" size={14} />
            <input
              ref={inputRef}
              type="search"
              value={query}
              placeholder="Chercher une page"
              aria-label="Chercher une page"
              aria-controls={listId}
              aria-activedescendant={results[cursor] ? `${listId}-${results[cursor].id}` : undefined}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onInputKeyDown}
            />
          </label>
          <ul id={listId} className="tools-menu__list" role="listbox" aria-label="Autres">
            {results.length === 0 ? (
              <li className="tools-menu__empty">Aucune page</li>
            ) : (
              results.map((tool, index) => (
                <li key={tool.id}>
                  <button
                    id={`${listId}-${tool.id}`}
                    type="button"
                    role="option"
                    aria-selected={index === cursor}
                    className={`tools-menu__option${index === cursor ? ' is-on' : ''}`}
                    onMouseEnter={() => {
                      setCursor(index);
                      if (tool.load) prefetchRoute(tool.load, tool.id);
                    }}
                    onClick={() => openTool(tool)}
                  >
                    <span className="tools-menu__ico" aria-hidden>
                      <Icon name={tool.icon} size={14} />
                    </span>
                    <span className="tools-menu__copy">
                      <span className="tools-menu__label">{highlight(tool.label, query)}</span>
                      <span className="tools-menu__hint">{tool.hint}</span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body
      )}
    </>
  );
};

function highlight(text: string, query: string) {
  const term = query.trim();
  if (!term) return text;
  const index = text.toLowerCase().indexOf(term.toLowerCase());
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + term.length)}</mark>
      {text.slice(index + term.length)}
    </>
  );
}

export default ToolsMenu;
